import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { captureDeletionReceipt, clearDeletionReceipt, deletionStatusLink, readDeletionReceipt, receiptStatus, type DeletionReceipt, type DeletionStatus } from '@/lib/deletion-receipt';
import './auth.css';
import './households.css';

export function DeletionStatusScreen({ initial }: { initial: ReturnType<typeof captureDeletionReceipt> }) {
  const [entry, setEntry] = useState(() => ({ value: initial.captured ? initial.value : readDeletionReceipt(), revision: 0 }));
  useEffect(() => {
    const consume = () => {
      const result = captureDeletionReceipt(location.href); if (!result.captured) return;
      history.replaceState(history.state, '', '/account-deletion');
      setEntry(old => ({ value: result.value, revision: old.revision + 1 }));
    };
    consume(); addEventListener('hashchange', consume); return () => removeEventListener('hashchange', consume);
  }, []);
  return <DeletionStatusContent key={`${entry.revision}:${entry.value?.requestId || 'missing'}`} value={entry.value} forget={() => { clearDeletionReceipt(undefined, entry.value); setEntry(old => ({ revision: old.revision + 1, value: undefined })); }} />;
}
function DeletionStatusContent({ value, forget }: { value?: DeletionReceipt; forget: () => void }) {
  const [state, setState] = useState<DeletionStatus>(), [error, setError] = useState(''), [busy, setBusy] = useState(false), [notice, setNotice] = useState('');
  const alive = useRef(true), lock = useRef(false), controller = useRef<AbortController | null>(null);
  const currentState = useRef(state); currentState.current = state;
  const complete = state?.phase === 'complete' && state.connectionsClosed;
  const canRetry = state?.phase === 'pending' && state.canRetry;
  async function refresh(retry = false) {
    if (!value || lock.current) return;
    if (retry && (currentState.current?.phase !== 'pending' || !currentState.current.canRetry)) return;
    lock.current = true; setBusy(true); setError(''); const active = new AbortController(); controller.current = active;
    const current = () => alive.current && controller.current === active && !active.signal.aborted;
    try { const result = await receiptStatus(value, retry, active.signal); if (current()) setState(result); }
    catch (cause) { if (current()) setError(cause instanceof Error ? cause.message : 'Status is unavailable. Try again.'); }
    finally { if (controller.current === active) { lock.current = false; if (alive.current) setBusy(false); } }
  }
  useEffect(() => {
    alive.current = true; void refresh();
    return () => { alive.current = false; controller.current?.abort(); controller.current = null; lock.current = false; };
  }, [value]);
  useEffect(() => {
    if (!value || state?.phase !== 'pending') return;
    const timer = setInterval(() => { if (document.visibilityState === 'visible') void refresh(); }, 15000);
    return () => clearInterval(timer);
  }, [value, state?.phase]);
  return <main className="auth-screen">
    <a className="auth-brand" href="/">streamit<span>.</span></a>
    <section className="household-invitation" aria-labelledby="deletion-status-title" aria-busy={busy}>
      <h1 id="deletion-status-title">Account deletion status</h1>
      {!value ? <p>Open your saved deletion status link in this browser. No sign-in is required.</p> : <>
        {complete ? <p role="status">Your Streamit account has been deleted and its remote connections closed.</p>
          : state?.phase === 'household_recovery' ? <p role="status">Sign-in is disabled. A shared household needs its remaining members to agree on a new owner before cleanup can finish.</p>
          : state ? <p role="status">Your account is disabled while deletion finishes. You can close this page and return using its saved link.</p> : !error && <p role="status">Checking deletion status…</p>}
        <p>Provider subscriptions and files in TorBox or other cloud services are kept. Shared households remain available to their other members.</p>
        {state?.error === 'TRAKT_REVOCATION_PENDING' && <p>Disconnecting Trakt is still being retried.</p>}
        {state?.error === 'REMOTE_DISCONNECT_PENDING' && <p>Closing a previous device connection is still being retried.</p>}
        {state?.error === 'CLERK_SETUP_REQUIRED' && <p>The sign-in service needs an administrator’s attention. Your Streamit access is already disabled.</p>}
        {state?.retryAt && state.phase !== 'complete' ? <p>Next retry is no earlier than {new Date(state.retryAt).toLocaleString()}.</p> : null}
        {error && <p role="alert" className="settings-error">{error}</p>}
        {notice && <p role="status">{notice}</p>}
        <div className="settings-fields">
          <Button onClick={() => void navigator.clipboard.writeText(deletionStatusLink(value)).then(() => setNotice('Private status link copied.')).catch(() => setError('Copying is unavailable. This browser still keeps your status link.'))}>Copy private status link</Button>
          {!complete && <>
            <Button variant="outline" disabled={busy} onClick={() => void refresh()}>Check again</Button>
            {canRetry && <Button disabled={busy} onClick={() => void refresh(true)}>Retry account cleanup</Button>}
          </>}
          {complete && <details><summary>More</summary><div className="settings-fields">
            <Button variant="outline" disabled={busy} onClick={() => void refresh()}>Check again</Button>
            <Button variant="outline" onClick={forget}>Forget status link on this browser</Button>
          </div></details>}
        </div>
      </>}
    </section>
    <nav aria-label="Account links"><a href="/">Back to Streamit</a><a href="/privacy.html">Privacy</a></nav>
  </main>;
}
